import { isBeautifulLinkBasedOnId } from './beautifiedPrefix';
import { SAFELINK_ORIGINALSRC_ATTRIBUTE_NAME } from './constants';
import { getSharingInfoToTryUpdateLink } from './getSharingInfoToTryUpdateLink';
import shouldProcessLink from './shouldProcessLink';
import trySetIdAndClassName from './trySetIdAndClassName';
import LinkPlugin from '../plugin/LinkPlugin';
import { LinkIdContainer } from '../schema/LinkIdContainer';
import { SharePointLink } from '../schema/SharePointLink';
import { AnchorElementsSource } from '../types/AnchorElementsSource';
import { logUsage } from 'owa-analytics';
import { isSharePointLink } from 'owa-sharepoint-link-detector';
import { trace } from 'owa-trace';

export default async function processAnchorElementsFromHtmlContent(
    allLinks: NodeListOf<Element> | Element[],
    hiddenReferenceAttachments: NodeListOf<Element> | Element[],
    source: AnchorElementsSource,
    linkPlugin: LinkPlugin,
    linkIdContainer: LinkIdContainer,
    linkIdsWithSpinner: string[],
    shouldChangeLinkId: boolean,
    shouldTryBeautify: boolean
) {
    // We need to set id and className for all sharePoint links so that getAllSharePointLinks can find them.
    const sharePointLinks: SharePointLink[] = [];
    for (let i = 0; i < allLinks.length; i++) {
        const link = allLinks[i] as HTMLAnchorElement;
        if (shouldProcessLink(link, hiddenReferenceAttachments)) {
            const url = getUrl(link);
            if (!isSharePointLink(url)) {
                continue;
            }

            trySetIdAndClassName(link, shouldChangeLinkId);
            sharePointLinks.push({ id: link.id, url: link.href, anchorElement: link });
        }
    }

    if (sharePointLinks.length < 10) {
        logUsage('UnderTenSPLinksWhenProcessAnchorElements', [
            source === AnchorElementsSource.PasteHtml,
            sharePointLinks.length,
        ]);
    } else {
        logUsage('AtLeastTenSPLinksWhenProcessAnchorElements', [
            source === AnchorElementsSource.PasteHtml,
        ]);
    }

    trace.info('process anchor elements total count: ' + sharePointLinks.length);
    for (let i = 0; i < sharePointLinks.length; i++) {
        const sharePointLink = sharePointLinks[i];
        const isLinkPreviouslyBeautified: boolean = isBeautifulLinkBasedOnId(
            sharePointLink.anchorElement
        );
        const isLinkPureAnchorElement: boolean = isPureAnchorElement(sharePointLink.anchorElement);
        const shouldBeautify: boolean =
            isEligibleForBeautifyLink(sharePointLink.anchorElement) &&
            isLinkPureAnchorElement &&
            shouldTryBeautify; // We only try to beautify for some links from paste
        const shouldRehydrate: boolean = isLinkPreviouslyBeautified || isLinkPureAnchorElement;
        logUsageForLinks(
            source,
            isLinkPreviouslyBeautified,
            isLinkPureAnchorElement,
            shouldBeautify
        );
        getSharingInfoToTryUpdateLink(
            linkPlugin,
            shouldBeautify,
            shouldRehydrate,
            isLinkPreviouslyBeautified,
            sharePointLink.anchorElement,
            linkIdContainer,
            i,
            linkIdsWithSpinner,
            source
        );
    }
}

function logUsageForLinks(
    source: AnchorElementsSource,
    isLinkPreviouslyBeautified: boolean,
    isLinkPureAnchorElement: boolean,
    shouldBeautify: boolean
) {
    let linkType: string = '';
    if (shouldBeautify) {
        linkType = 'shouldBeautify';
    } else if (isLinkPreviouslyBeautified) {
        linkType = 'isLinkPreviouslyBeautified';
    } else if (isLinkPureAnchorElement) {
        linkType = 'isLinkPureAnchorElement';
    }
    if (source === AnchorElementsSource.PasteHtml) {
        logUsage('ComposeSPLinksFromPaste', [linkType]);
    } else {
        logUsage('ComposeSPLinksFromDraft', [linkType]);
    }
}

export function getUrl(link: HTMLAnchorElement): string {
    const url: string = link.getAttribute(SAFELINK_ORIGINALSRC_ATTRIBUTE_NAME);
    if (url && url.length > 0) {
        return url;
    }

    return link.href;
}

export function isEligibleForBeautifyLink(link: HTMLAnchorElement): boolean {
    let url: string = link.getAttribute(SAFELINK_ORIGINALSRC_ATTRIBUTE_NAME);
    if (!url) {
        url = link.href;
    }

    return url.toLowerCase() === link.text.toLowerCase();
}

function isPureAnchorElement(link: HTMLAnchorElement): boolean {
    return !link.children || link.children.length === 0;
}
